import { Injectable } from "@angular/core";
import * as XLSX from "xlsx";

export interface ExportColumn {
  readonly label: string;
  readonly value?: string;
}

export type ExportHeader = ExportColumn | string;

export interface ExportSheet {
  readonly name: string;
  readonly header: readonly ExportHeader[];
  readonly data: readonly unknown[];
}

export interface SkuJourneyExportRow {
  readonly name: unknown;
  readonly y: unknown;
  readonly y1: unknown;
}

type WorksheetRow = unknown[];

function asWorksheetRows(data: readonly unknown[]): WorksheetRow[] {
  return data.filter(Array.isArray).map((row) => [...row]);
}

function isSkuJourneyRow(value: unknown): value is SkuJourneyExportRow {
  return typeof value === "object" && value !== null && "name" in value;
}

@Injectable({ providedIn: "root" })
export class ExportService {
  exportToExcelSheets(sheets: readonly ExportSheet[], fileName: string): void {
    const workbook = XLSX.utils.book_new();

    sheets.forEach(({ name, header, data }) => {
      const tableData = this.parseTableData(header, data);
      const worksheet = XLSX.utils.aoa_to_sheet([
        tableData.header,
        ...tableData.data,
      ]);
      XLSX.utils.book_append_sheet(workbook, worksheet, name);
    });

    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }

  exportToExcel(
    excelHeader: readonly ExportHeader[],
    excelData: readonly unknown[],
    fileName: string,
  ): void {
    let worksheet: XLSX.WorkSheet;
    const merges: XLSX.Range[] = [];
    const rows = asWorksheetRows(excelData);

    switch (fileName) {
      case "summaryTable":
        worksheet = XLSX.utils.aoa_to_sheet(rows);
        merges.push(
          { s: { r: 0, c: 1 }, e: { r: 0, c: 3 } },
          { s: { r: 0, c: 4 }, e: { r: 0, c: 7 } },
        );
        break;
      case "portfolioHealthChart":
        worksheet = XLSX.utils.aoa_to_sheet(rows);
        merges.push(
          { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } },
          { s: { r: 0, c: 3 }, e: { r: 0, c: 5 } },
        );
        break;
      case "portfolioEfficiency":
      case "phi_bar_chart_data":
        worksheet = XLSX.utils.aoa_to_sheet([
          excelHeader.map((column) =>
            typeof column === "string" ? column : column.label,
          ),
          ...rows,
        ]);
        break;
      case "prePostportfolioRecommendation": {
        worksheet = XLSX.utils.aoa_to_sheet(rows);
        const postRecommendationIndex = rows.findIndex((row) =>
          row.includes("Post-Recommendation"),
        );
        merges.push(
          { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
          {
            s: { r: postRecommendationIndex, c: 0 },
            e: { r: postRecommendationIndex, c: 5 },
          },
        );
        break;
      }
      case "skuJourneyTable": {
        const formattedData = excelData
          .filter(isSkuJourneyRow)
          .map((item) => [item.name, item.y, item.y1]);
        if (formattedData.length === 0) {
          console.error("No data to export for skuJourneyTable.");
          return;
        }
        worksheet = XLSX.utils.aoa_to_sheet(formattedData);
        break;
      }
      case "first-tail":
      case "second-tail":
      case "third-tail":
      case "fourth-tail":
        worksheet = XLSX.utils.aoa_to_sheet(rows);
        break;
      default: {
        const tableData = this.parseTableData(excelHeader, excelData);
        worksheet = XLSX.utils.aoa_to_sheet([
          tableData.header,
          ...tableData.data,
        ]);
        break;
      }
    }

    if (merges.length > 0) {
      worksheet["!merges"] = merges;
    }
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }

  parseTableData(
    excelHeader: readonly ExportHeader[],
    excelData: readonly unknown[],
  ): { header: string[]; data: unknown[][] } {
    const columns = excelHeader.filter(
      (column): column is ExportColumn => typeof column !== "string",
    );
    const header = columns.map(({ label }) => label);
    const data = excelData.map((item) => {
      const row =
        typeof item === "object" && item !== null
          ? (item as Readonly<Record<string, unknown>>)
          : {};
      return columns.map(({ value }) => (value ? row[value] : undefined));
    });
    return { header, data };
  }
}
